/**
 * Mobile Upload Page - Bulk Generator
 *
 * CSV upload and field mapping for bulk content generation:
 * - Step 1: Upload CSV file
 * - Step 2: Map CSV columns to app fields (Tap-to-Assign)
 * - Step 3: Processing/Success state
 */

import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UploadCloud,
  FileSpreadsheet,
  Check,
  ArrowRight,
  AlertCircle,
  Database,
  X,
  Loader2,
  Home,
} from 'lucide-react';

// App fields that CSV columns can be mapped to
const REQUIRED_FIELDS = [
  { id: 'topic', label: 'Topic / Title', required: true },
  { id: 'keywords', label: 'Keywords', required: false },
  { id: 'tone', label: 'Tone', required: false },
];

interface MobileUploadPageProps {
  onNavigateHome?: () => void;
}

export const MobileUploadPage: React.FC<MobileUploadPageProps> = ({
  onNavigateHome,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [fileName, setFileName] = useState('');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [mappings, setMappings] = useState<Record<string, string | null>>({});
  const [rowCount, setRowCount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Parse CSV headers from file
  const parseCSVHeaders = (file: File): Promise<{ headers: string[]; rowCount: number }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter((line) => line.trim());
        if (lines.length === 0) {
          reject(new Error('Empty file'));
          return;
        }
        const headers = lines[0].split(',').map((h) => h.trim().replace(/"/g, ''));
        resolve({ headers, rowCount: lines.length - 1 });
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    try {
      const { headers, rowCount } = await parseCSVHeaders(file);
      setFileName(file.name);
      setCsvHeaders(headers);
      setRowCount(rowCount);
      setMappings({});
      setStep(2);
    } catch {
      alert('Failed to parse CSV file');
    }
  };

  const handleMapField = (appFieldId: string, csvHeader: string) => {
    setMappings((prev) => ({
      ...prev,
      [appFieldId]: prev[appFieldId] === csvHeader ? null : csvHeader,
    }));
  };

  const handleGenerate = async () => {
    if (!mappings['topic']) {
      alert("Please map a CSV column to 'Topic / Title'");
      return;
    }

    setIsProcessing(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsProcessing(false);
    setStep(3);
  };

  const handleReset = () => {
    setStep(1);
    setFileName('');
    setCsvHeaders([]);
    setMappings({});
    setRowCount(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Step 1: Upload
  const renderUploadStep = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex-1 flex flex-col justify-center items-center px-6"
    >
      <div className="w-24 h-24 bg-purple-50 rounded-full flex items-center justify-center mb-6 border border-purple-100">
        <UploadCloud className="w-12 h-12 text-purple-600" />
      </div>
      <h2 className="text-xl font-bold text-slate-900 mb-2">Bulk Generator</h2>
      <p className="text-slate-500 text-center mb-8 px-4 leading-relaxed">
        Upload a CSV file to generate hundreds of articles at once. We'll help you map the columns.
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileSelect}
        className="hidden"
        id="csv-upload"
      />
      <label
        htmlFor="csv-upload"
        className="w-full bg-white border-2 border-dashed border-purple-300 rounded-2xl p-8 flex flex-col items-center cursor-pointer hover:bg-purple-50 active:bg-purple-100 transition-colors"
      >
        <FileSpreadsheet className="w-8 h-8 text-purple-600" />
        <span className="text-purple-600 font-semibold mt-3">Select CSV File</span>
        <span className="text-slate-400 text-sm mt-1">or drag and drop</span>
      </label>
    </motion.div>
  );

  // Step 2: Mapping
  const renderMappingStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex-1 overflow-auto px-5 pt-4 pb-32"
    >
      {/* File Info */}
      <div className="flex items-center gap-2 mb-6 p-3 bg-slate-100 rounded-xl">
        <FileSpreadsheet className="w-5 h-5 text-slate-500" />
        <span className="text-slate-600 font-medium flex-1 truncate">{fileName}</span>
        <span className="text-slate-400 text-sm">{rowCount} rows</span>
        <button onClick={handleReset} className="p-1 hover:bg-slate-200 rounded-full">
          <X className="w-4 h-4 text-slate-400" />
        </button>
      </div>

      <h3 className="text-lg font-bold text-slate-900 mb-4">Map Your Columns</h3>

      {/* Mapping Cards */}
      {REQUIRED_FIELDS.map((field) => {
        const isMapped = !!mappings[field.id];
        return (
          <div
            key={field.id}
            className="mb-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm"
          >
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-1">
                <span className="font-bold text-slate-800 text-base">{field.label}</span>
                {field.required && <span className="text-red-500">*</span>}
              </div>
              {isMapped ? (
                <span className="flex items-center gap-1 bg-green-100 px-2 py-1 rounded text-green-700 text-xs font-bold">
                  <Check className="w-3 h-3" />
                  Mapped
                </span>
              ) : field.required ? (
                <span className="flex items-center gap-1 bg-amber-100 px-2 py-1 rounded text-amber-700 text-xs font-bold">
                  <AlertCircle className="w-3 h-3" />
                  Required
                </span>
              ) : (
                <span className="text-slate-400 text-xs">Optional</span>
              )}
            </div>

            {/* CSV Headers - Horizontal Scroll */}
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
              {csvHeaders.map((header) => {
                const isSelected = mappings[field.id] === header;
                return (
                  <button
                    key={header}
                    onClick={() => handleMapField(field.id, header)}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg border text-sm transition-all ${
                      isSelected
                        ? 'bg-purple-600 border-purple-600 text-white font-bold'
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {header}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </motion.div>
  );

  // Step 3: Success
  const renderSuccessStep = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex-1 flex flex-col justify-center items-center px-6"
    >
      <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6">
        <Database className="w-12 h-12 text-green-600" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Processing...</h2>
      <p className="text-slate-500 text-center mb-8 leading-relaxed">
        We are generating content for{' '}
        <span className="font-bold text-slate-900">{rowCount} rows</span>. You will receive a
        notification when complete.
      </p>

      <div className="space-y-3 w-full max-w-xs">
        <button
          onClick={onNavigateHome || handleReset}
          className="w-full px-8 py-3 bg-slate-900 rounded-xl text-white font-bold flex items-center justify-center gap-2 hover:bg-slate-800 active:scale-[0.98] transition-all"
        >
          <Home className="w-5 h-5" />
          Return Home
        </button>
        <button
          onClick={handleReset}
          className="w-full px-8 py-3 bg-white border border-slate-200 rounded-xl text-slate-700 font-semibold hover:bg-slate-50 active:scale-[0.98] transition-all"
        >
          Upload Another File
        </button>
      </div>
    </motion.div>
  );

  return (
    <div className="flex flex-col min-h-full bg-slate-50 pb-24">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-100 bg-white flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <UploadCloud className="w-5 h-5 text-purple-600" />
            Bulk Import
          </h1>
          {step === 2 && (
            <p className="text-slate-500 text-sm">Map your CSV columns to ContentSage fields</p>
          )}
        </div>
        <span className="text-slate-400 font-bold text-sm">Step {step}/3</span>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {step === 1 && renderUploadStep()}
        {step === 2 && renderMappingStep()}
        {step === 3 && renderSuccessStep()}
      </AnimatePresence>

      {/* Footer Action (Step 2 only) */}
      {step === 2 && (
        <div className="fixed bottom-0 left-0 right-0 p-5 bg-white border-t border-slate-100 shadow-2xl z-20 pb-[calc(env(safe-area-inset-bottom)+1.25rem)]">
          <button
            onClick={handleGenerate}
            disabled={!mappings['topic'] || isProcessing}
            className={`w-full h-14 rounded-xl flex items-center justify-center gap-2 transition-all ${
              mappings['topic'] && !isProcessing
                ? 'bg-purple-600 shadow-lg shadow-purple-200 hover:bg-purple-700 active:scale-[0.98]'
                : 'bg-slate-300 cursor-not-allowed'
            }`}
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 text-white animate-spin" />
                <span className="text-white font-bold text-lg">Processing...</span>
              </>
            ) : (
              <>
                <span className="text-white font-bold text-lg">Start Generation</span>
                <ArrowRight className="w-5 h-5 text-white" />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default MobileUploadPage;
